package data.objects

import apiclient.FormationClient
import apiclient.customfields.parseFieldValues
import apiclient.customfields.tagValues
import apiclient.geoobjects.ChangeTitle
import apiclient.geoobjects.ConnectableGeoShapeChange
import apiclient.geoobjects.Content
import apiclient.geoobjects.DeleteContent
import apiclient.geoobjects.EventInvitation
import apiclient.geoobjects.ExternalIdChanges
import apiclient.geoobjects.ExternalObjectUpdate
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.LatLon
import apiclient.geoobjects.MarkerColor
import apiclient.geoobjects.MarkerIcon
import apiclient.geoobjects.MarkerShape
import apiclient.geoobjects.MeetingAttendee
import apiclient.geoobjects.MeetingInvitationStatus
import apiclient.geoobjects.NewEvent
import apiclient.geoobjects.NewPoi
import apiclient.geoobjects.NewTask
import apiclient.geoobjects.ObjectChange
import apiclient.geoobjects.ObjectChanges
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.ObjectVisibility
import apiclient.geoobjects.SetMarkerColor
import apiclient.geoobjects.SetMarkerIcon
import apiclient.geoobjects.SetMarkerShape
import apiclient.geoobjects.SetObjectVisibility
import apiclient.geoobjects.TaskState
import apiclient.geoobjects.TimeTrigger
import apiclient.geoobjects.UpdatePointLocation
import apiclient.geoobjects.UpdateZoneGeometry
import apiclient.geoobjects.UpsertMarkdown
import apiclient.geoobjects.UpsertObjectLink
import apiclient.geoobjects.UpsertPoll
import apiclient.geoobjects.UpsertSvgImage
import apiclient.geoobjects.UpsertTaskTemplate
import apiclient.geoobjects.UpsertWebLink
import apiclient.geoobjects.addInvitees
import apiclient.geoobjects.applyObjectChanges
import apiclient.geoobjects.changeInviteStatus
import apiclient.geoobjects.changeTaskState
import apiclient.geoobjects.createEvent
import apiclient.geoobjects.createPOI
import apiclient.geoobjects.createTask
import apiclient.geoobjects.deleteGeneralMarker
import apiclient.geoobjects.pointGeometry
import apiclient.geoobjects.removeInvitees
import apiclient.geoobjects.restArchiveObject
import apiclient.geoobjects.restChangeType
import apiclient.geoobjects.restDeleteObjectById
import apiclient.geoobjects.restFlagObject
import apiclient.geoobjects.restGetObjectById
import apiclient.geoobjects.restUnArchiveObject
import apiclient.geoobjects.restUnFlagObject
import apiclient.geoobjects.updateOrCreateExternalObjects
import apiclient.inventorymanagement.AssetInformation
import apiclient.reversegeocoder.reverseGeocode
import apiclient.tags.getTagValues
import apiclient.tags.getUniqueTag
import apiclient.tags.parseTag
import apiclient.users.PublicUserProfile
import apiclient.users.UserProfileSummary
import apiclient.users.toUserProfileSummary
import auth.ApiUserStore
import auth.CurrentWorkspaceStore
import com.jillesvangurp.geojson.translate
import data.objects.views.ColorSelectionStore
import data.objects.views.IconSelectionStore
import data.objects.views.ShapeSelectionStore
import data.objects.views.attachments.AttachmentsStore
import data.objects.views.attachments.PreAttachmentsStore
import data.objects.views.attachments.RemovedAttachmentsStore
import data.objects.views.attachments.toPreAttachment
import data.users.ActiveUserStore
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.invoke
import dev.fritz2.core.storeOf
import dev.fritz2.tracking.tracker
import geofenceeditor.GeoShapeStore
import koin.koinCtx
import koin.withKoin
import kotlin.time.Duration.Companion.days
import kotlinx.coroutines.CoroutineName
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import localization.TL
import localization.Translation
import mainmenu.Pages
import mainmenu.RouterStore
import map.MapLayersStore
import maplibreGL.FormationMap
import maplibreGL.joinNullableLists
import mapsearch.MapRenderOverrides
import mapsearch.buildings.CurrentBuildingsStore
import mapsearch.generalMarkers.GeneralMarkersSearchSpecStore
import mapsearch.generalMarkers.connectableshapes.NewConnectableShapeConnectionStore
import mapsearch.insertInCaches
import mapsearch.removeInCaches
import mapsearch.updateInCaches
import mapsearch.zones.ZoneSearchSpecStore
import model.L
import model.PreAttachment
import model.ScannedCode
import model.getFirstGroupIdOrNull
import overlays.BusyStore
import overlays.BusyStoreTexts
import qrcode.ManualCodeInputStore
import qrcode.ScannedCodeStore
import search.searchlayer.MapSearchClientsStore
import utils.apiScope
import utils.calculateEndDate
import utils.combineToInstant
import utils.extractDurationInMinutes
import utils.formatDateForDatePicker
import utils.formatTimeForTimePicker
import utils.getName
import utils.parseInstant
import utils.roundTo

val emptyUserProfileSummary = UserProfileSummary(
    userId = "",
    firstName = null,
    lastName = null,
    jobTitle = null,
)
val emptyGeoObjectDetails = GeoObjectDetails(
    id = "",
    ownerId = "",
    objectType = ObjectType.TransientMarker,
    createdAt = "",
    updatedAt = "",
    latLon = LatLon(0.0, 0.0),
    title = "",
    owner = emptyUserProfileSummary,
)
private val defaultTaskReminder = 1.days.inWholeMinutes.toInt()
val defaultEventReminders = listOf(10, 60)

class ActiveObjectStore : RootStore<GeoObjectDetails>(
    initialData = emptyGeoObjectDetails,
    job = Job(),
) {

    private val formationMap: FormationMap by koinCtx.inject()
    private val mapRenderOverrides: MapRenderOverrides by koinCtx.inject()
    private val activeUserStore: ActiveUserStore by koinCtx.inject()
    private val formationClient: FormationClient by koinCtx.inject()
    private val apiUserStore: ApiUserStore by koinCtx.inject()
    private val descriptionInputFieldStore: DescriptionInputFieldStore by koinCtx.inject()
    private val datePickerStore: DatePickerStore by koinCtx.inject()
    private val timePickerStore: TimePickerStore by koinCtx.inject()
    private val durationPickerStore: DurationPickerStore by koinCtx.inject()
    private val activeObjectKeywordsStore: ActiveObjectKeywordsStore by koinCtx.inject()
    private val activeObjectFieldValuesStore: ActiveObjectFieldValuesStore by koinCtx.inject()
    private val attendeesSelectorStore: AttendeesSelectorStore by koinCtx.inject()
    private val assigneeSelectorStore: AssigneeSelectorStore by koinCtx.inject()
    private val currentBuildingsStore: CurrentBuildingsStore by koinCtx.inject()

    private val colorSelectionStore: ColorSelectionStore by koinCtx.inject()
    private val iconSelectionStore: IconSelectionStore by koinCtx.inject()
    private val shapeSelectionStore: ShapeSelectionStore by koinCtx.inject()
    private val geoShapeStore: GeoShapeStore by koinCtx.inject()
    private val attachmentsStore: AttachmentsStore by koinCtx.inject()
    private val preAttachmentsStore: PreAttachmentsStore by koinCtx.inject()
    private val removedAttachmentsStore: RemovedAttachmentsStore by koinCtx.inject()
    private val translation: Translation by koinCtx.inject()
    private val routerStore: RouterStore by koinCtx.inject()
    private val mapSearchClientsStore: MapSearchClientsStore by koinCtx.inject()
    private val scannedCodeStore: ScannedCodeStore by koinCtx.inject()
    private val newConnectableShapeConnectionStore: NewConnectableShapeConnectionStore by koinCtx.inject()
    private val mapLayersStore: MapLayersStore by koinCtx.inject()
    private val currentWorkspaceStore by koinCtx.inject<CurrentWorkspaceStore>()
    private val busyStore by koinCtx.inject<BusyStore>()

    private val activeObjectId = map(GeoObjectDetails.L.id)
    private val attachments = map(GeoObjectDetails.L.attachments)
    private val tags = map(GeoObjectDetails.L.tags)

    val createTracker = tracker()
    val updateTracker = tracker()
    val deleteTracker = tracker()
    val attachmentTracker = tracker()

    var initialObject = emptyGeoObjectDetails
//    private var lastActiveObjectMarker: GeoObjectDetails? = null

    val updateActiveObject = handle<GeoObjectDetails> { _, newGeoObject ->
        initialObject = if (newGeoObject.objectType == ObjectType.GeneralMarker) {
            newGeoObject.copy(shape = newGeoObject.shape ?: MarkerShape.Pentagon)
        } else {
            newGeoObject
        }
        newGeoObject
    }

    val duplicate = handle { current ->
        updateChangeInputStores()
        current.attachments?.let { attachments ->
            preAttachmentsStore.addOrUpdateMultiple(
                attachments.mapNotNull {
                    it.toPreAttachment()
                },
            )
            preAttachmentsStore.saveAsInterimState()
        }
        attachmentsStore.reset()
        val duplicate = current.copy(
            id = "", taskState = null, attachments = null,
            tags = current.tags.filter {
                val (tagName, _) = it.parseTag()
                tagName !in listOf(
                    ObjectTags.ExternalId,
                    ObjectTags.ActionId,
                    ObjectTags.Flagged,
                    ObjectTags.Archived,
                    ObjectTags.NoAutoArchive,
                    ObjectTags.Deleted,
                ).map { t -> t.name }
            },
        )
        initialObject = duplicate.copy(assignedTo = null)
        update(duplicate)
        routerStore.validateInternalRoute(mapOf("add" to current.objectType.name))
        duplicate
    }

    val createNew = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            val currentLat = formationMap.getCenter().lat
            val currentLon = formationMap.getCenter().lng
            val newPosition = LatLon(lat = currentLat, lon = currentLon)
            val assignee = assigneeSelectorStore.current?.firstOrNull()?.userId
            val attendees = attendeesSelectorStore.current?.map {
                EventInvitation(userId = it, remindBeforeMinutes = defaultEventReminders)
            }
            val now = Clock.System.now()
            val currentObjectType = current.objectType

            console.log("Create object:", current.title)

            apiScope.launch {
                val groupId = apiUserStore.current.getFirstGroupIdOrNull() ?: ""
                busyStore.handleApiCall(
                    supplier = suspend {
                        createTracker.track {
                            when (currentObjectType) {
                                ObjectType.POI -> formationClient.createPOI(
                                    groupId = groupId,
                                    poi = NewPoi(
                                        latLon = newPosition,
                                        connectedToId = currentBuildingsStore.getConnectedToId(),
                                        title = current.title,
                                        description = current.description?.ifBlank { null },
                                        keywords = current.keywords,
                                        fieldValueTags = activeObjectFieldValuesStore.current.tagValues(),
                                        color = current.color,
                                        iconCategory = current.iconCategory,
                                        shape = current.shape,
                                    ),
                                )

                                ObjectType.Task -> formationClient.createTask(
                                    group = groupId,
                                    task = NewTask(
                                        title = current.title,
                                        latLon = newPosition,
                                        connectedToId = currentBuildingsStore.getConnectedToId(),
                                        description = current.description?.ifBlank { null },
                                        atTime = current.atTime,
                                        assignedTo = assignee,
                                        keywords = current.keywords,
                                        fieldValueTags = activeObjectFieldValuesStore.current.tagValues(),
                                        iconCategory = MarkerIcon.Task,
                                        color = MarkerColor.PurpleTask,
                                        shape = MarkerShape.Circle,
                                        reminders = listOfNotNull(
                                            assignee?.let { TimeTrigger(it, defaultTaskReminder) },
                                            apiUserStore.current.userId.let {
                                                TimeTrigger(
                                                    it,
                                                    defaultTaskReminder,
                                                )
                                            },
                                        ).distinct(),
                                    ),
                                )

                                ObjectType.Event -> formationClient.createEvent(
                                    groupId = groupId,
                                    event = NewEvent(
                                        title = current.title,
                                        latLon = newPosition,
                                        connectedToId = currentBuildingsStore.getConnectedToId(),
                                        atTime = current.atTime ?: now.toString(),
                                        endTime = current.endTime,
                                        description = current.description?.ifBlank { null },
                                        attendees = attendees,
                                        keywords = current.keywords,
                                        fieldValueTags = activeObjectFieldValuesStore.current.tagValues(),
                                        iconCategory = MarkerIcon.Event,
                                        color = MarkerColor.TurquoiseMeeting,
                                        shape = MarkerShape.Circle,
                                    ),
                                )

                                ObjectType.ObjectMarker -> {
                                    (scannedCodeStore.current.extOrIntObjIdOrActionId
                                        ?: current.tags.getUniqueTag(ObjectTags.ExternalId))?.let { code ->

                                        formationClient.updateOrCreateExternalObjects(
                                            groupId,
                                            listOf(
                                                ExternalObjectUpdate(
                                                    externalId = code,
                                                    updatePointLocation = UpdatePointLocation(
                                                        zoneId = currentBuildingsStore.pointWithinFloor(newPosition),
                                                        position = newPosition,
                                                    ),
                                                    assetInformation = AssetInformation(
                                                        displayName = current.title,
                                                    ),
                                                ),
                                            ),
                                        ).getOrElse {
                                            // FIXME error handling
                                            console.log(it)
                                            null
                                        }?.let {
                                            val id = it.entries.first().value
                                            formationClient.restGetObjectById(id)
                                        }
                                    }
                                }

                                ObjectType.Zone -> formationClient.createPOI(
                                    groupId = groupId,
                                    poi = NewPoi(
                                        latLon = newPosition,
                                        connectedToId = currentBuildingsStore.getConnectedToId(),
                                        title = current.title,
                                        description = current.description?.ifBlank { null },
                                        keywords = current.keywords,
                                        fieldValueTags = activeObjectFieldValuesStore.current.tagValues(),
                                    ),
                                )

                                else -> {
                                    console.log("ObjectType not yet supported!")
                                    null
                                }
                            }
                        }
                    },
                    successMessage = translation[
                        TL.AlertNotifications.OBJECT_SUCCESSFULLY_CREATED,
                        mapOf(
                            "object" to currentObjectType.getName(),
                        ),
                    ],
                    processResult = { response ->
                        println("done!\n$response")
                        // FIXME Hack to create zone
                        if (current.objectType == ObjectType.Zone && response.objectType == ObjectType.POI) {
                            // convert POI to zone
                            busyStore.handleApiCall(
                                supplier = suspend {
                                    formationClient.restChangeType(response.id, ObjectType.Zone)
                                },
                                processResult = { zone ->
                                    insertInCaches(zone)
                                    mapRenderOverrides.pushActiveObject(obj = zone)
                                    updateActiveObject(zone)
                                },
                                processError = { error ->
                                    console.log("Failed to change POI to Zone", error)
                                },
                            )
                        } else {
                            insertInCaches(response)
                            mapRenderOverrides.pushActiveObject(obj = response, isNew = true)
                            updateActiveObject(response)
                        }
                        scannedCodeStore.reset()
                    },
                    errorMessage = translation[TL.AlertNotifications.OBJECT_CREATION_FAILED, mapOf("object" to currentObjectType.getName())],
                )

            }
        }
    }

    val applyObjectChanges = SimpleHandler<ObjectChanges?> { data, _ ->
        data handledBy { changes ->
            if (changes != null) {
                busyStore.handleApiCall(
                    supplier = {
                        formationClient.applyObjectChanges(listOf(changes))
                    },
                    processResult = { geoObjects ->
                        if (geoObjects.isNotEmpty()) {
                            // update the current active geoObject
                            val updated = geoObjects.firstOrNull { it.id == current.id }
                            console.log("Applied ObjectChanges successfully.", changes)
                            updated?.let { updatedActiveObj ->
                                pushActiveObjectAndUpdateInCache(updatedActiveObj)
                                updateActiveObject(updatedActiveObj)
                            }
                            // also update other geoObjects
                            geoObjects.filter { it.id != current.id }.forEach { otherUpdated ->
//                            insertObjectInCachesAndMap(otherUpdated)
                                updateInCaches(otherUpdated)
                            }
                        }
                    },
                    processError = { error ->
                        console.warn("Failed to apply ObjectChange:", changes.changes, "With error:", error)
                    },
                )
            }
        }
    }

    fun handleEdits() {
        val currentLat = formationMap.getCenter().lat
        val currentLon = formationMap.getCenter().lng
        val newPosition = LatLon(lat = currentLat, lon = currentLon)
        val currentObjectType = current.objectType
        val newColor = colorSelectionStore.current
        val newIcon = iconSelectionStore.current
        val newShape = shapeSelectionStore.current
        val newZoneGeometry = geoShapeStore.current?.geometry(newPosition)

        val init = initialObject
        // Create a fake object to bridge in api call network time
        val fakeMarker = init.copy(
            latLon = newPosition,
            color = newColor,
            iconCategory = newIcon,
            shape = newShape,
            geometry = if (currentObjectType == ObjectType.Zone) {
                newZoneGeometry
            } else {
                current.geometry?.translate(newPosition.pointGeometry())
            },
            geoReferencedConnectableObject = current.geoReferencedConnectableObject,
        )
        // mark the object as hidden and hide active object marker, while edit is active
        // check hiddenIds whenever mapSearch tries to render objects

        // show active object marker after update
        // -> use a fake updated version
        // insert fake version in cache and display it on the map
        // -> silently replace fake version with the real updated version (on Success) or fallback to version before update (on Failure)
        // insert real version in cache (and on failure display on the map, because ideally it does not differ from success version)
        //
        mapRenderOverrides.removeHiddenOverride(fakeMarker.id)
        updateInCaches(fakeMarker)
        update(fakeMarker)
        editMarkerAndPosition()
    }

    val editMarkerAndPosition = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            val currentLat = formationMap.getCenter().lat
            val currentLon = formationMap.getCenter().lng
            val newPosition = LatLon(lat = currentLat, lon = currentLon)
            val currentObjectType = current.objectType
            val newColor = colorSelectionStore.current
            val newIcon = iconSelectionStore.current
            val newShape = shapeSelectionStore.current
            val newZoneGeometry = geoShapeStore.current?.geometry(newPosition)
            val newGeoReferencedConnectableObject = current.geoReferencedConnectableObject

            val init = initialObject

            routerStore.back()

            apiScope.launch {
                if (current.id.isNotBlank()) {
                    when (currentObjectType) {
                        ObjectType.GeneralMarker, ObjectType.ObjectMarker, ObjectType.POI -> {
                            apiScope.launch {
                                apiUserStore.current.apiUser?.groups?.firstOrNull()?.groupId?.let { groupId ->
                                    busyStore.handleApiCall(
                                        supplier = {
                                            formationClient.reverseGeocode(
                                                groupId = groupId,
                                                position = newPosition,
                                                parentId = currentBuildingsStore.getConnectedToId(),
                                            )
                                        },
                                        processResult = { response ->
                                            console.log("Reversed Geocode Success!", response)
                                            val connectedToId = response.best?.id

                                            val changeList = joinNullableLists(
                                                listOf(
                                                    listOfNotNull(
                                                        if (newIcon != init.iconCategory) {
                                                            SetMarkerIcon(newIcon)
                                                        } else {
                                                            null
                                                        },
                                                        if (newColor != init.color) {
                                                            SetMarkerColor(newColor)
                                                        } else {
                                                            null
                                                        },
                                                        newGeoReferencedConnectableObject?.let { geoObj ->
                                                            ConnectableGeoShapeChange.RotateShape(geoObj.rotation)
                                                        },
                                                        UpdatePointLocation(
                                                            position = newPosition,
                                                            enableGeocoding = true,
                                                            locationId = connectedToId ?: currentBuildingsStore.getConnectedToId(),
                                                        ),
                                                    ),
                                                    // Currently these cannot be changed for markers created from archetype
                                                    if (currentObjectType != ObjectType.GeneralMarker) {
                                                        listOfNotNull(
                                                            if (newShape != init.shape) {
                                                                SetMarkerShape(newShape)
                                                            } else {
                                                                null
                                                            },
                                                        )
                                                    } else null,
                                                ),
                                            )
                                            busyStore.handleApiCall(
                                                supplier = {
                                                    updateTracker.track {
                                                        formationClient.applyObjectChanges(
                                                            ObjectChanges(
                                                                current.id,
                                                                changeList,
                                                            ),
                                                        )
                                                    }
                                                },
                                                processResult = { geoObjects ->
                                                    if (geoObjects.isNotEmpty()) {
                                                        val updated = geoObjects.firstOrNull { it.id == current.id }
                                                        console.log("Applied ObjectChanges successfully.", changeList)
                                                        updated?.let { updatedActiveObj ->
//                                                                mapRenderOverrides.removeHiddenOverride(updatedActiveObj)
                                                            mapRenderOverrides.removeLastObjects()
                                                            updateActiveObject(updatedActiveObj)
                                                            pushActiveObjectAndUpdateInCache(updatedActiveObj)
//                                                                mapRenderOverrides.pushActiveObject(updatedActiveObj)
                                                            formationMap.off(type = "click", fnId = "resetOnMapClick")
                                                            formationMap.once(type = "click", fn = ::resetOnMapClick, fnId = "resetOnMapClick")
                                                        }

                                                        // also update other geoObjects
                                                        geoObjects.filter { it.id != current.id }
                                                            .forEach { otherUpdated ->
                                                                updateInCaches(otherUpdated)
                                                            }
                                                    }
                                                },
                                                successMessage = translation[
                                                    TL.AlertNotifications.OBJECT_SUCCESSFULLY_UPDATED,
                                                    mapOf(
                                                        "object" to currentObjectType.getName(),
                                                    ),
                                                ],
                                                processError = { error ->
                                                    console.warn(
                                                        "Failed to apply ObjectChanges:",
                                                        changeList.toTypedArray(),
                                                        "With error:",
                                                        error,
                                                    )
                                                    // Revert back to initial Object, when request failed
                                                    updateActiveObject(init)
                                                    pushActiveObjectAndUpdateInCache(init)
                                                },
                                                errorMessage = translation[TL.AlertNotifications.OBJECT_UPDATE_FAILED, mapOf("object" to currentObjectType.getName())],
                                            )
                                        },
                                        errorMessage = translation[TL.AlertNotifications.OBJECT_UPDATE_FAILED, mapOf("object" to currentObjectType.getName())],
                                        processError = { error ->
                                            console.warn("Failed to get reversed geocode.", error.message)
                                        },
                                    )
                                }
                            }
                        }

                        else -> {
                            val changeList = joinNullableLists(
                                listOf(
                                    listOfNotNull(
                                        if (newIcon != init.iconCategory) {
                                            SetMarkerIcon(newIcon)
                                        } else {
                                            null
                                        },
                                        if (newColor != init.color) {
                                            SetMarkerColor(newColor)
                                        } else {
                                            null
                                        },
                                        if (newShape != init.shape) {
                                            SetMarkerShape(newShape)
                                        } else {
                                            null
                                        },
                                        // Always perform LocationUpdate, to update the connectedTo field and tag,
                                        // even if the position has not changed
                                        UpdatePointLocation(
                                            position = newPosition,
                                            locationId = currentBuildingsStore.getConnectedToId(),
                                        ),
                                    ),
                                    // Handle ZoneGeometry
                                    listOfNotNull(
                                        if (
                                            currentObjectType == ObjectType.Zone
                                            && initialObject.geometry != newZoneGeometry
                                            && newZoneGeometry != null
                                        ) {
                                            UpdateZoneGeometry(
                                                geometry = newZoneGeometry,
                                                zoneId = currentBuildingsStore.getConnectedToId(),
                                            )
                                        } else null,
                                    ),
                                ),
                            )

                            busyStore.handleApiCall(
                                supplier = {
                                    updateTracker.track {
                                        formationClient.applyObjectChanges(
                                            ObjectChanges(
                                                current.id,
                                                changeList,
                                            ),
                                        )
                                    }
                                },
                                processResult = { geoObjects ->
                                    if (geoObjects.isNotEmpty()) {
                                        val updated = geoObjects.firstOrNull { it.id == current.id }
                                        console.log("Applied ObjectChanges successfully.", changeList)
                                        updated?.let { updatedActiveObj ->
//                                                mapRenderOverrides.removeHiddenOverride(updatedActiveObj.id)
                                            mapRenderOverrides.removeLastObjects()
                                            updateActiveObject(updatedActiveObj)
                                            pushActiveObjectAndUpdateInCache(updatedActiveObj)
//                                                mapRenderOverrides.pushActiveObject(updatedActiveObj)
                                            formationMap.off(type = "click", fnId = "resetOnMapClick")
                                            formationMap.once(type = "click", fn = ::resetOnMapClick, fnId = "resetOnMapClick")
                                        }

                                        // also update other geoObjects
                                        geoObjects.filter { it.id != current.id }
                                            .forEach { otherUpdated ->
                                                updateInCaches(otherUpdated)
                                            }
                                    }
                                },
                                successMessage = translation[
                                    TL.AlertNotifications.OBJECT_SUCCESSFULLY_UPDATED,
                                    mapOf(
                                        "object" to currentObjectType.getName(),
                                    ),
                                ],
                                processError = { error ->
                                    console.warn(
                                        "Failed to apply ObjectChanges:",
                                        changeList.toTypedArray(),
                                        "With error:",
                                        error,
                                    )
                                },
                            )
                        }
                    }
                } else {
                    mapRenderOverrides.removeHiddenOverride(current.id)
                    console.log("Object has no id!")
                }
            }
            // Also show UserMarker again here:
            mapRenderOverrides.removeHiddenOverride(apiUserStore.current.userId)
            formationMap.off(type = "click", fnId = "resetOnMapClick")
            mapRenderOverrides.removeHiddenOverride(current.id)
        }
    }

    private fun getContentChanges(): List<ObjectChange>? {

        // Removed Content
        val removed = removedAttachmentsStore.current.map { (attachmentId, attachment) ->
            when (attachment) {
                is Content.Image -> DeleteContent(attachmentId)
                is Content.GeoObject -> UpsertObjectLink(
                    id = attachmentId,
                    objectId = null,
                    title = "",
                )

                is Content.GeoReferencedImage -> DeleteContent(attachmentId)
                is Content.Markdown -> UpsertMarkdown(attachmentId)
                is Content.Poll -> UpsertPoll(
                    id = attachmentId,
                    title = "",
                    pollOptions = null,
                )

                is Content.ScanToCreateTask -> DeleteContent(attachmentId)
                is Content.SvgImage -> UpsertSvgImage(
                    id = attachmentId,
                    svgContent = null,
                    title = null,
                )

                is Content.WebLink -> UpsertWebLink(
                    id = attachmentId,
                    href = null,
                    title = "",
                )

                else -> DeleteContent(attachmentId)
            }
        }.takeIf { it.isNotEmpty() }

        // Changed or Added Content
        val changedOrAdded = preAttachmentsStore.current.values.mapNotNull { preAttachment ->
            when (preAttachment) {
                is PreAttachment.PreMarkdown -> {
                    UpsertMarkdown(
                        id = preAttachment.id,
                        title = preAttachment.title,
                        content = preAttachment.text,
                    )
                }

                is PreAttachment.PreWebLink -> {
                    UpsertWebLink(
                        id = preAttachment.id,
                        href = preAttachment.href,
                        title = preAttachment.title,
                        rel = preAttachment.rel?.name,
                        actionId = preAttachment.actionId,
                    )
                }

                is PreAttachment.PrePoll -> {
                    UpsertPoll(
                        id = preAttachment.id,
                        title = preAttachment.title,
                        pollOptions = preAttachment.options,
                        actionId = preAttachment.actionId,
                    )
                }

                is PreAttachment.PreGeoObject -> UpsertObjectLink(
                    id = preAttachment.id,
                    objectId = preAttachment.objectId,
                    title = preAttachment.title,
                    rel = preAttachment.rel?.name,
                )

                is PreAttachment.PreImage -> null
                is PreAttachment.PreScanToCreateTask -> {
                    val template = preAttachment.taskTemplate
                    template?.let {
                        UpsertTaskTemplate(
                            id = preAttachment.id,
                            title = preAttachment.title,
                            taskTemplate = template,
                            actionId = null,
                        )
                    }
                }

                is PreAttachment.PreSvgImage -> null
            }
        }.takeIf { it.isNotEmpty() }

        return joinNullableLists(listOf(removed, changedOrAdded)).takeIf { it.isNotEmpty() }
    }

    private fun gatherNewAndDeletedFieldValueTags(): List<String> {
        return currentWorkspaceStore.current?.fieldDefinitions?.let { definitions ->
            initialObject.tags.parseFieldValues(definitions)
        }?.let { initialFieldValues ->
            val changedFieldValues = activeObjectFieldValuesStore.current
            // Get all deleted fieldValues and map them to empty values
            val deletedFieldValues = initialFieldValues.filter { oldFieldValue ->
                oldFieldValue.field !in changedFieldValues.map { newFieldValue -> newFieldValue.field }
            }.map { "${it.field}:" }
            changedFieldValues.tagValues() + deletedFieldValues
        } ?: emptyList()
    }

    val delete = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            val currentObject = current
            console.log("Delete object:", currentObject)

            val currentObjectType = currentObject.objectType

            routerStore.goToMap()

            apiScope.launch {
                if (currentObject.id.isNotBlank()) {
                    busyStore.handleApiCall(
                        supplier = suspend {
                            deleteTracker.track {
                                when (currentObjectType) {
                                    ObjectType.GeneralMarker -> {
                                        apiUserStore.current.apiUser?.groups?.firstOrNull()?.groupId?.let { groupId ->
                                            formationClient.deleteGeneralMarker(workspaceId = groupId, id = currentObject.id)
                                        }
                                    }

                                    else -> {
                                        formationClient.restDeleteObjectById(id = currentObject.id)
                                    }
                                }
                            }
                        },
                        successMessage = translation[
                            TL.AlertNotifications.OBJECT_SUCCESSFULLY_DELETED,
                            mapOf(
                                "object" to currentObjectType.getName(),
                            ),
                        ],
                        processResult = { response ->
                            println("done!\n${response}")
                            mapRenderOverrides.removeLastObjects()
                            removeInCaches(currentObject)
                        },
                        errorMessage = translation[
                            TL.AlertNotifications.OBJECT_DELETION_FAILED,
                            mapOf(
                                "object" to currentObjectType.getName(),
                            ),
                        ],
                        processError = { response ->
                            println("object deletion failed!\n${response.message}")
                        },
                    )
                }
            }
            resetStore()
            formationMap.off(type = "click", fnId = "resetOnMapClick")
        }
    }

    val resetStore = handle { current ->
        // Also show UserMarker again here:
        // apiUserStore.current.userId
        initialObject = emptyGeoObjectDetails
        resetChangeInputStores()
        emptyGeoObjectDetails
    }

    private val saveChanges = handle { current ->
        initialObject = current
        current
    }

    val resetActiveObjectAndUser = handle { current ->
        mapRenderOverrides.removeHiddenOverride(current.id)
        resetStore()
        activeUserStore.reset()
        scannedCodeStore.reset()
        newConnectableShapeConnectionStore.reset()
        formationMap.off(type = "click", fnId = "resetOnMapClick")
        current
    }

    val resetActiveObjectAndUserToMap = handle { current ->
        coroutineScope {
            launch {
                routerStore.goToMap()
                resetActiveObjectAndUser()
            }
        }
        current
    }

    fun resetOnMapClick() {
        console.log("Map click -> resetOnMapClick")
        resetActiveObjectAndUserToMap()
    }

    val revertMarkerChanges = SimpleHandler<Unit> { data, _ ->
        withKoin {
            val generalMarkerSearchSpecStore: GeneralMarkersSearchSpecStore = get()
            val zoneSearchSpecStore: ZoneSearchSpecStore = get()

            data handledBy {
                formationMap.off(type = "click", fnId = "resetOnMapClick")
                formationMap.once(type = "click", fn = ::resetOnMapClick, fnId = "resetOnMapClick")
                mapRenderOverrides.removeHiddenOverrides(setOf(initialObject.id, apiUserStore.current.userId))
                insertInCaches(initialObject)
                update(initialObject)
                when (current.objectType) {
                    ObjectType.Zone -> {
                        zoneSearchSpecStore.updateZoneGeometry(geoObject = initialObject)
                    }

                    ObjectType.GeneralMarker -> {
                        generalMarkerSearchSpecStore.updateGeneralMarkerGeometry(initialObject)
                    }

                    else -> {}
                }
                colorSelectionStore.resetTo(current.objectType)
                iconSelectionStore.resetTo(current.objectType)
                shapeSelectionStore.resetTo(current.objectType)
            }
        }
    }

    private val resetChangeInputStores = handle { current ->
        descriptionInputFieldStore.reset()
        datePickerStore.reset()
        timePickerStore.reset()
        attendeesSelectorStore.reset()
        assigneeSelectorStore.reset()
        colorSelectionStore.resetTo(current.objectType)
        iconSelectionStore.resetTo(current.objectType)
        shapeSelectionStore.resetTo(current.objectType)
        activeObjectKeywordsStore.reset()
        activeObjectFieldValuesStore.reset()
        current
    }

    val updateChangeInputStores = handle { current ->
        current.description?.let { description -> descriptionInputFieldStore.update(description) }
            ?: descriptionInputFieldStore.update("")
        current.atTime?.parseInstant()?.let { atTime ->
            datePickerStore.update(atTime.formatDateForDatePicker())
            timePickerStore.update(atTime.formatTimeForTimePicker())
            current.endTime?.parseInstant()?.let { endTime ->
                durationPickerStore.update(extractDurationInMinutes(atTime, endTime).toInt())
            } ?: durationPickerStore.update(30)
        } ?: run {
            datePickerStore.update("")
            timePickerStore.update("")
            durationPickerStore.update(30)
        }
        current.attendees?.let { attendees -> attendeesSelectorStore.update(attendees.map { it.userId }.toSet()) }
            ?: attendeesSelectorStore.update(null)
        current.assignedTo?.let { assignee -> assigneeSelectorStore.updateUserIds(listOf(assignee)) }
            ?: assigneeSelectorStore.update(null)
        current.color?.let { color -> colorSelectionStore.update(color) }
            ?: colorSelectionStore.resetTo(current.objectType)
        current.iconCategory?.let { icon -> iconSelectionStore.update(icon) }
            ?: iconSelectionStore.resetTo(current.objectType)
        current.shape?.let { shape -> shapeSelectionStore.update(shape) }
            ?: shapeSelectionStore.resetTo(current.objectType)
        current.keywords?.let { keywords -> activeObjectKeywordsStore.update(keywords) }
            ?: activeObjectKeywordsStore.reset()
        currentWorkspaceStore.current?.fieldDefinitions?.let { definitions ->
            current.tags.parseFieldValues(definitions)
        }?.let { fieldValues ->
            activeObjectFieldValuesStore.update(fieldValues)
        } ?: run { activeObjectFieldValuesStore.reset() }
        current.attachments?.let { attachments ->
            attachmentsStore.update(
                attachments.filter { attachment ->
                    attachment.id !in removedAttachmentsStore.interimState
                }.associateBy { it.id },
            )
        } ?: attachmentsStore.reset()
        current
    }

    val readFromChangeInputStores = handle { current ->
        val newAttendeeList = with(attendeesSelectorStore.current) {
            if (!this.isNullOrEmpty()) {
                val remaining = current.attendees?.filter { attendee -> this.contains(attendee.userId) }
                val remainingIds = remaining?.map { attendee -> attendee.userId }
                val new = this.filter { id -> !(remainingIds ?: listOf()).contains(id) }
                    .map { id -> MeetingAttendee(userId = id, MeetingInvitationStatus.Pending, optional = false) }
                (remaining ?: listOf()) + new
            } else null
        }
        val newAssignee =
            with(assigneeSelectorStore.current) { if (!this.isNullOrEmpty()) this.firstOrNull() else null }

        current.copy(
            description = descriptionInputFieldStore.current,
            atTime = combineToInstant(datePickerStore.current, timePickerStore.current)?.toString(),
            endTime = calculateEndDate(
                combineToInstant(datePickerStore.current, timePickerStore.current),
                durationPickerStore.current,
            ),
            assignedToUser = newAssignee?.toUserProfileSummary(),
            assignedTo = newAssignee?.userId,
            attendees = newAttendeeList,
            keywords = activeObjectKeywordsStore.current,
            color = colorSelectionStore.current,
            iconCategory = iconSelectionStore.current,
            shape = shapeSelectionStore.current,
        )
    }

    val setAttendee = handle<String> { current, attendeeId ->
        current.copy(
            attendees = listOf(
                MeetingAttendee(
                    userId = attendeeId,
                    MeetingInvitationStatus.Pending,
                    optional = false,
                ),
            ),
        )
    }

    val setAssignee = handle<PublicUserProfile> { current, userProfile ->
        current.copy(
            assignedToUser = userProfile.toUserProfileSummary(),
            assignedTo = userProfile.userId,
        )
    }

    var lastInputRotation = storeOf(current.geoReferencedConnectableObject?.rotation?.roundTo(1), job = job)

    val setConnectedObjectRotation = SimpleHandler<Double> { data, _ ->
        data handledBy { rotation ->
            val rotated = if (current.geoReferencedConnectableObject?.rotation != rotation) {
//                console.log("UPDATE ROTATION", rotation, rotation.roundTo(2))
                current.copy(
                    geoReferencedConnectableObject = current.geoReferencedConnectableObject?.copy(
                        rotation = rotation.roundTo(2),
                    ),
                )
            } else current
            mapRenderOverrides.pushActiveObject(rotated)
            update(rotated)
        }
    }

    val setConnectedObjectRotationFromInput = handle<Double> { current, rotation ->
        if (current.geoReferencedConnectableObject?.rotation != rotation) {
            console.log("UPDATE ROTATION FROM INPUT", rotation, rotation.roundTo(2))
            lastInputRotation.update(rotation)
            current.copy(
                geoReferencedConnectableObject = current.geoReferencedConnectableObject?.copy(
                    rotation = rotation.roundTo(2),
                ),
            )
        } else current
    }

    val centerMapToObject = handle { current ->
        if (current.latLon != LatLon(0.0, 0.0)) {
            formationMap.panTo(center = LatLon(current.latLon.lat, current.latLon.lon))
        }
        current
    }

    val focusThisObject = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            if (current.id.isNotBlank()) {
                formationMap.flyTo(center = LatLon(current.latLon.lat, current.latLon.lon), zoom = formationMap.getZoom())
            }
            updateChangeInputStores()
        }
    }

    val removeMapClickListener = handle { current ->
        formationMap.off(type = "click", fnId = "resetOnMapClick")
        current
    }

    val releaseTrackedObject = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            val oldExternalIds = current.tags.getTagValues(ObjectTags.ExternalId)
            if (oldExternalIds.size > 1) {
                console.warn("released multiple externalIds", oldExternalIds.toString())
            }
            busyStore.handleApiCall(
                supplier = suspend {
                    formationClient.applyObjectChanges(
                        changes = listOf(
                            ObjectChanges(
                                objectId = current.id,
                                oldExternalIds.map { id ->
                                    ExternalIdChanges.ReleaseExternalId(id)
                                },
                            ),
                        ),
                    )
//                formationClient.releaseTrackedObject(
//                    ownerId = currentWorkspaceStore.current?.groupId ?: error("groupId should not be null"),
//                    objectId = old.id,
//                )
                },
                successMessage = translation[BusyStoreTexts.Success],
                processResult = {
                    resetStore()
                    routerStore.goToMap()
                },
            )
        }
    }

    val archiveObject = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            busyStore.handleApiCall(
                supplier = suspend {
                    formationClient.restArchiveObject(objectId = current.id)
                },
                successMessage = translation[BusyStoreTexts.Success],
                processResult = { archivedObj ->
//                    removeInCaches(archivedObj)
                    resetStore()
                    routerStore.goToMap()
                },
            )
        }
    }

    val unArchiveObject = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            busyStore.handleApiCall(
                supplier = suspend {
                    formationClient.restUnArchiveObject(objectId = current.id)
                },
                successMessage = translation[BusyStoreTexts.Success],
                processResult = { unArchivedObj ->
                    updateInCaches(unArchivedObj)
                    resetStore()
                    routerStore.goToMap()
                },
                withBusyState = true,
            )
        }
    }

    val flagObject = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            busyStore.handleApiCall(
                supplier = suspend {
                    formationClient.restFlagObject(objectId = current.id)
                },
                successMessage = translation[BusyStoreTexts.Success],
                processResult = { flaggedObj ->
//                    removeInCaches(flaggedObj)
                    resetStore()
                    routerStore.goToMap()
                },
            )
        }
    }

    val unflagObject = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            busyStore.handleApiCall(
                supplier = suspend {
                    formationClient.restUnFlagObject(objectId = current.id)
                },
                successMessage = translation[BusyStoreTexts.Success],
                processResult = { unflaggedObj ->
                    updateInCaches(unflaggedObj)
                    resetStore()
                    routerStore.goToMap()
                },
            )
        }
    }

    val hideObjectMarker = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            busyStore.handleApiCall(
                supplier = suspend {
                    formationClient.applyObjectChanges(
                        ObjectChanges(
                            current.id,
                            listOf(SetObjectVisibility(ObjectVisibility.MarkerHidden)),
                        ),
                    )
                },
                successMessage = translation[BusyStoreTexts.Success],
                processResult = { geoObjects ->
                    geoObjects.firstOrNull()?.tags?.let { tagList ->
                        tags.update(tagList)
                    }
                    CoroutineScope(CoroutineName("refresh-objects")).launch {
                        mapSearchClientsStore.updateMapSearchClients(null)
                    }
                },
            )
        }
    }

    val showObjectMarker = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            busyStore.handleApiCall(
                supplier = suspend {
                    formationClient.applyObjectChanges(
                        ObjectChanges(
                            current.id,
                            listOf(SetObjectVisibility(null)),
                        ),
                    )
                },
                successMessage = translation[BusyStoreTexts.Success],
                processResult = { geoObjects ->
                    geoObjects.firstOrNull()?.tags?.let { tagList ->
                        tags.update(tagList)
                    }
                    CoroutineScope(CoroutineName("refresh-objects")).launch {
                        mapSearchClientsStore.updateMapSearchClients(null)
                    }
                },
            )
        }
    }

    val connectQRCodeToObject = handle { current ->
        val oldExternalIds = current.tags.getTagValues(ObjectTags.ExternalId)
        if (oldExternalIds.isNotEmpty()) {
            removeQRcode()
            addQRcode()
        } else {
            addQRcode()
        }
        current
    }

    private suspend fun addQRcode() {
        val groupId = currentWorkspaceStore.current?.groupId
        val scannedCodeStore by koinCtx.inject<ScannedCodeStore>()
        val manualCodeInputStore by koinCtx.inject<ManualCodeInputStore>()
        scannedCodeStore.current.extOrIntObjIdOrActionId?.let { externalId ->
            groupId?.let { gId ->
                busyStore.handleApiCall(
                    suspend {
                        formationClient.applyObjectChanges(
                            changes = listOf(
                                ObjectChanges(
                                    objectId = current.id,
                                    ExternalIdChanges.AssociateExternalId(externalId),
                                ),
                            ),
                        )
                    },
                    processResult = {
                        resetStore()
                        manualCodeInputStore.reset()
                        CoroutineScope(CoroutineName("refresh-objects")).launch {
                            delay(1500)
                            mapSearchClientsStore.updateMapSearchClients(null)
                        }
                        scannedCodeStore.reset()
                        routerStore.goToMap()
                    },
                    withBusyState = true,
                    successMessage = flowOf("QR code successfully connected."),
                    errorMessage = flowOf("QR code connection failed."),
                )
            }
        }
    }

    private suspend fun removeQRcode() {

        val oldExternalIds = current.tags.getTagValues(ObjectTags.ExternalId)
        if (oldExternalIds.size > 1) {
            console.warn("would remove multiple externalIds")
        } else {
            busyStore.handleApiCall(
                suspend {
                    formationClient.applyObjectChanges(
                        changes = listOf(
                            ObjectChanges(
                                objectId = current.id,
                                oldExternalIds.map { id ->
                                    ExternalIdChanges.RemoveExternalId(id)
                                },
                            ),
                        ),
                    )
                },
                processResult = { o ->
                    console.log("Removed externalIds:", oldExternalIds.toString())
                },
                withBusyState = true,
                successMessage = flowOf("Removed externalIds: $oldExternalIds"),
                errorMessage = flowOf("Removing QR code failed."),
            )
        }
    }

    val removeQRCodeFromObject = handle { old ->
        val oldExternalIds = current.tags.getTagValues(ObjectTags.ExternalId)
        currentWorkspaceStore.current?.groupId?.let { gId ->
            busyStore.handleApiCall(
                suspend {
                    formationClient.applyObjectChanges(
                        changes = listOf(
                            ObjectChanges(
                                objectId = current.id,
                                oldExternalIds.map { id ->
                                    ExternalIdChanges.RemoveExternalId(id)
                                },
                            ),
                        ),
                    )
                },
                processResult = {
                    resetStore()
                    routerStore.goToMap()
                },
                withBusyState = true,
                successMessage = translation[BusyStoreTexts.Success],
                errorMessage = flowOf("Removing QR code failed."),
            )
        }
        old
    }

    val setTaskStatus = handle<TaskState> { current, newStatus ->
        if (current.canModify && current.taskState != newStatus) { //current.canManage &&
            current.copy(taskState = newStatus)
        } else current
    }

    val resetTaskStatus = handle { current ->
        if (current.canModify) {
            current.copy(taskState = initialObject.taskState)
        } else current
    }

    val changeTaskStatus = handle { current ->
        if (current.taskState != null) {
            apiScope.launch {
                updateTracker.track {
                    if (current.id != "") {
                        busyStore.handleApiCall(
                            supplier = suspend {
                                formationClient.changeTaskState(id = current.id, state = current.taskState!!)
                            },
                            successMessage = translation[
                                TL.AlertNotifications.OBJECT_STATE_SUCCESSFULLY_CHANGED,
                                mapOf(
                                    "object" to current.objectType.getName(),
                                ),
                            ],
                            processResult = { response ->
                                println("task state changed!\n$response")
                                mapSearchClientsStore.invalidateLayerCaches(current.objectType)
                                initialObject = response
                                mapLayersStore.setUpdatedHit(response)
                            },
                            errorMessage = translation[
                                TL.AlertNotifications.OBJECT_STATE_CHANGE_FAILED,
                                mapOf(
                                    "object" to current.objectType.getName(),
                                ),
                            ],
                        )
                    }
                }
            }
        }
        current
    }

    val updateAttendeesLegacyApi = handle<Pair<List<EventInvitation>?, List<String>>> { current, (newInvites, obsoleteInvites) ->
        if (current.id.isNotBlank()) {
            if (newInvites != null) {
                console.log("new", newInvites.map { it.userId }.toString())
            }
            console.log("remove", obsoleteInvites.toString())

            apiScope.launch {
                updateTracker.track {
                    busyStore.handleApiCall(
                        supplier = suspend {
                            val addResp = if (!newInvites.isNullOrEmpty()) {
                                formationClient.addInvitees(current.id, newInvites)
                            } else null

                            val removeResp = if (obsoleteInvites.isNotEmpty()) {
                                formationClient.removeInvitees(current.id, obsoleteInvites)
                            } else null

                            when {
                                removeResp != null -> removeResp
                                addResp != null -> addResp
                                else -> null
                            }
                        },
                        successMessage = translation[TL.AlertNotifications.MEETING_INVITATIONS_SUCCESSFULLY_UPDATED],
                        processResult = { response ->
                            println("Changed meeting invitations!\n$response")
                            updateActiveObject(response)
                            saveChanges()
                        },
                        errorMessage = translation[TL.AlertNotifications.MEETING_INVITATIONS_UPDATE_FAILED],
                    )
                }
            }
        }
        current
    }

    val setMeetingStatus = handle<MeetingInvitationStatus> { current, newStatus ->
        val currentUserId = apiUserStore.current.userId
        val userIsAttendee = currentUserId in (current.attendees?.map { it.userId } ?: emptyList())
        if (userIsAttendee) {
            current.copy(
                attendees = (current.attendees?.filter { it.userId != currentUserId } ?: emptyList())
                    + listOf(MeetingAttendee(currentUserId, newStatus, optional = false)),
            )
        } else current
    }

    val resetMeetingStatus = handle { current ->
        val currentUserId = apiUserStore.current.userId
        val userIsAttendee = currentUserId in (current.attendees?.map { it.userId } ?: emptyList())
        val initialMeetingState =
            initialObject.attendees?.firstOrNull { it.userId == currentUserId }?.meetingInvitationStatus
        if (userIsAttendee && initialMeetingState != null) {
            current.copy(
                attendees = (current.attendees?.filter { it.userId != currentUserId } ?: emptyList())
                    + listOf(MeetingAttendee(currentUserId, initialMeetingState, optional = false)),
            )
        } else current
    }

    val changeMeetingStatus = handle { current ->
        val currentUserId = apiUserStore.current.userId
        val userIsAttendee = currentUserId in (current.attendees?.map { it.userId } ?: emptyList())
        if (userIsAttendee) {
            val currentStatus = current.attendees?.find { it.userId == currentUserId }?.meetingInvitationStatus
            apiScope.launch {
                updateTracker.track {
                    busyStore.handleApiCall(
                        supplier = suspend {
                            currentStatus?.let { status ->
                                formationClient.changeInviteStatus(
                                    current.id,
                                    currentUserId,
                                    status,
                                )
                            }
                        },
                        successMessage = translation[
                            TL.AlertNotifications.OBJECT_SUCCESSFULLY_UPDATED,
                            mapOf(
                                "object" to current.objectType.getName(),
                            ),
                        ],
                        processResult = { response ->
                            initialObject = response
                            mapLayersStore.setUpdatedHit(response)
                        },
                        errorMessage = translation[
                            TL.AlertNotifications.OBJECT_UPDATE_FAILED,
                            mapOf(
                                "object" to current.objectType.getName(),
                            ),
                        ],
                    )
                }
            }
        }
        current
    }

    val hideActiveObjectMarker = handle { current ->
        mapRenderOverrides.addHiddenOverride(current.id)
        current
    }

    val prefillTitleWithCode = handle { current ->
        prepareMarkerCustomizationStores(ObjectType.ObjectMarker, scannedCodeStore.current)
        scannedCodeStore.current.extOrIntObjIdOrActionId?.let { code -> current.copy(title = code) } ?: current
    }

    private fun prepareMarkerCustomizationStores(objType: ObjectType? = null, scannedCode: ScannedCode? = null) {
        val type = objType ?: current.objectType
        scannedCode?.color?.let { colorSelectionStore.update(it) } ?: kotlin.run { colorSelectionStore.resetTo(type) }
        scannedCode?.icon?.let { iconSelectionStore.update(it) } ?: kotlin.run { iconSelectionStore.resetTo(type) }
        scannedCode?.shape?.let { shapeSelectionStore.update(it) } ?: kotlin.run { shapeSelectionStore.resetTo(type) }
        scannedCode?.tag?.let { activeObjectKeywordsStore.add(it) }
    }

    val readFieldValues = handle<List<String>> { current, tags ->
        currentWorkspaceStore.current?.fieldDefinitions?.let { definitions ->
            tags.parseFieldValues(definitions)
        }?.let { fieldValues ->
            activeObjectFieldValuesStore.update(fieldValues)
        }
        current
    }

    fun pushActiveObjectAndUpdateInCache(obj: GeoObjectDetails) {
        mapRenderOverrides.pushActiveObject(obj)
        updateInCaches(obj)
    }

    private val setActiveObjectOnMap = SimpleHandler<GeoObjectDetails> { data, _ ->
        data handledBy { obj ->
            console.log("UPDATE ACTIVE OBJECT CHANGED", obj.title)
            if (obj.id.isBlank()) {
                delay(100)
                mapRenderOverrides.pushActiveObject(null)
            } else {
                mapRenderOverrides.removeHiddenOverride(obj.id)
                mapRenderOverrides.pushActiveObject(obj)
            }
        }
    }

    val editTitle = handle<String> { current, newTitle ->
        newTitle.takeIf { it != current.title }?.let { title ->
            applyObjectChanges(ObjectChanges(current.id, ChangeTitle(title)))
            current.copy(title = title)
        } ?: current
    }

    private val onObjIdChange = SimpleHandler<String> { data, _ ->
        data handledBy { id ->
            setActiveObjectOnMap(current)
            if (id.isNotBlank()) {
                val objId = current.tags.getUniqueTag(ObjectTags.ExternalId) ?: id
                // Put objId into the router url if it is not blank
                if (objId.isNotBlank()) { // && parseEnumValue<Pages>(routerStore.current["page"]) == Pages.Map) {
                    if (current.objectType != ObjectType.GeneralMarker) {
                        formationMap.off(type = "click", fnId = "resetOnMapClick")
                        formationMap.once(type = "click", fn = ::resetOnMapClick, fnId = "resetOnMapClick")
                    }
//                    console.log("Gotcha! ActiveObjId changed. Add id to route.")
                    routerStore.validateInternalRoute(
                        routerStore.baseRoute() + Pages.Map.route + mapOf("id" to objId),
                    )
                }
            }
        }
    }

    init {
        attachments.data.map { attachments ->
            attachments?.associateBy { it.id } ?: emptyMap()
        } handledBy attachmentsStore.update
        tags.data handledBy readFieldValues
        // using id to fire handlers if the whole active object changes
        activeObjectId.data handledBy onObjIdChange
//        mapRenderOverrides.hiddenMarkers.data handledBy { ids ->
//            mapMarkerSubscription.removeRenderedMarkersFromMapInAllTiles(ids)
//        }
    }
}
